/**
 * Shows a soft (non-blocking) warning banner when a product added to the cart by the
 * "Product Personalizer" (Zepto) app doesn't meet the theme's minimum purchase quantity.
 *
 * Product Personalizer adds its personalized items to the cart through its own flow,
 * bypassing the theme's quantity selector entirely, so the minimum enforced there
 * (see quantity.liquid / buy-buttons.liquid / cart-products.liquid) never applies to it.
 * Product Personalizer only exposes one relevant hook, `pplrAddToCartCompleted`, which
 * fires AFTER the item is already in the cart and has no documented payload — so this
 * can only warn the shopper after the fact, not block the add. See the app's docs:
 * https://productpersonalizer.com/docs/how-to-use-product-personalizer-custom-event-hooks/
 */

const MINIMUM_QUANTITY = Number(Theme?.settings?.quick_order_minimum_quantity) || 0;
const WARNING_TAG_NAME = 'personalizer-minimum-warning';
const AUTO_DISMISS_MS = 10000;

/**
 * Extracts the pieces-per-box count from a variant title (e.g. "6 pzas" -> 6).
 * Mirrors the logic in snippets/variant-pieces.liquid. Falls back to 1.
 * @param {string | null | undefined} variantTitle
 * @returns {number}
 */
function getPiecesPerBox(variantTitle) {
  if (!variantTitle) return 1;

  for (const word of variantTitle.split(' ')) {
    const number = parseInt(word, 10);
    if (Number.isFinite(number) && number > 0) return number;
  }

  return 1;
}

/**
 * @typedef {Object} CartLineItem
 * @property {number} product_id
 * @property {string} product_title
 * @property {string} [variant_title]
 * @property {number} quantity
 */

/**
 * The minimum only applies to personalized lines (a non-internal, non-empty property was
 * set). Plain purchases without personalization are never restricted by this setting.
 * @param {CartLineItem} item
 * @returns {boolean}
 */
function isPersonalized(item) {
  const properties = /** @type {Record<string, string> | undefined} */ (item.properties);
  if (!properties) return false;

  return Object.entries(properties).some(([key, value]) => !key.startsWith('_') && String(value ?? '').trim() !== '');
}

/**
 * @param {{ items: CartLineItem[] }} cart
 * @returns {Map<number, { title: string, pieces: number }>}
 */
function getPieceTotalsByProduct(cart) {
  /** @type {Map<number, { title: string, pieces: number }>} */
  const totals = new Map();

  for (const item of cart.items) {
    if (!isPersonalized(item)) continue;

    const piecesPerBox = getPiecesPerBox(item.variant_title);
    const existing = totals.get(item.product_id) || { title: item.product_title, pieces: 0 };
    existing.pieces += item.quantity * piecesPerBox;
    totals.set(item.product_id, existing);
  }

  return totals;
}

async function handlePersonalizerAddToCart() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return;

    const cart = await response.json();
    const totalsByProduct = getPieceTotalsByProduct(cart);

    for (const { title, pieces } of totalsByProduct.values()) {
      if (pieces > 0 && pieces < MINIMUM_QUANTITY) {
        showWarning(title, pieces, MINIMUM_QUANTITY);
      }
    }
  } catch (error) {
    console.error('[personalizer-minimum-warning]', error);
  }
}

/**
 * @param {string} productTitle
 * @param {number} current
 * @param {number} minimum
 */
function showWarning(productTitle, current, minimum) {
  const missing = minimum - current;
  const template =
    Theme?.translations?.personalizer_minimum_warning ||
    '{{ product }}: {{ current }} / {{ minimum }} ({{ missing }} missing)';

  const message = template
    .replace('{{ product }}', productTitle)
    .replace('{{ current }}', current.toString())
    .replace('{{ minimum }}', minimum.toString())
    .replace('{{ missing }}', missing.toString());

  /** @type {PersonalizerMinimumWarning} */
  let banner = document.querySelector(WARNING_TAG_NAME);
  if (!banner) {
    banner = document.createElement(WARNING_TAG_NAME);
    document.body.appendChild(banner);
  }

  banner.setMessage(message);
}

class PersonalizerMinimumWarning extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dismissTimeout;

  connectedCallback() {
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');
  }

  /** @param {string} message */
  setMessage(message) {
    this.textContent = '';

    const text = document.createElement('span');
    text.className = 'personalizer-minimum-warning__text';
    text.textContent = message;
    this.appendChild(text);

    const dismissButton = document.createElement('button');
    dismissButton.type = 'button';
    dismissButton.className = 'personalizer-minimum-warning__dismiss';
    dismissButton.setAttribute('aria-label', 'Dismiss');
    dismissButton.textContent = '×';
    dismissButton.addEventListener('click', () => this.remove());
    this.appendChild(dismissButton);

    clearTimeout(this.#dismissTimeout);
    this.#dismissTimeout = setTimeout(() => this.remove(), AUTO_DISMISS_MS);
  }
}

if (!customElements.get(WARNING_TAG_NAME)) {
  customElements.define(WARNING_TAG_NAME, PersonalizerMinimumWarning);
}

if (MINIMUM_QUANTITY > 0) {
  window.addEventListener('pplrAddToCartCompleted', handlePersonalizerAddToCart);
}

const style = document.createElement('style');
style.textContent = `
  ${WARNING_TAG_NAME} {
    position: fixed;
    inset-block-start: 1rem;
    inset-inline: 1rem;
    margin-inline: auto;
    max-width: 32rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: var(--color-background, #fff);
    color: var(--color-foreground, #1a1a1a);
    border: var(--style-border-width, 1px) solid var(--color-border, #ccc);
    border-radius: var(--style-border-radius, 8px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  ${WARNING_TAG_NAME} .personalizer-minimum-warning__text {
    flex: 1 1 auto;
  }

  ${WARNING_TAG_NAME} .personalizer-minimum-warning__dismiss {
    flex: 0 0 auto;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 1.25rem;
    line-height: 1;
    color: inherit;
    padding: 0;
  }
`;
document.head.appendChild(style);
